import { CompleteTask } from "../CompleteTask/CompleteTask";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { CbmImpl } from "~/modules/cbm";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getReservationsByTask, isCallTask, sendTrackingEvent } from "../../ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { Action, TaskReservation } from "~/modules/actions/Action";
import { WrapUpTask } from "../WrapUpTask/WrapUpTask";



















export class EndTask implements Action<Promise<TaskReservation>> {
    


    #taskSid: string;

    constructor(taskSid: string) {
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`endTask invoked with taskSid: ${this.#taskSid}`);

        if (!worker) {
            const errorMsg = "endTask: worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");
        const wrappingReservation = reservations.find((res) => res.status === "wrapping");

        if (!reservation && !wrappingReservation) {
            const errorMsg = `endTask: Reservation for task ${this.#taskSid} is not found`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, { error: errorMsg, taskSid: this.#taskSid });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            if (reservation && isCallTask(reservation.task)) {
                const updatedReservation = await cbm.endConference(reservation);

                sendTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid
                });
                return { task: updatedReservation.task, reservation: updatedReservation };
            }
            if (wrappingReservation) {
                const completedTask = await new CompleteTask(this.#taskSid).run(ctx);

                sendTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: wrappingReservation.sid
                });
                return Promise.resolve({ task: completedTask, reservation: wrappingReservation });
            }
            const { task, reservation: wrappedReservation } = await new WrapUpTask(this.#taskSid).run(ctx);

            sendTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: wrappedReservation.sid
            });

            return Promise.resolve({ task, reservation: wrappedReservation });
        } catch (error) {
            const errorMsg = `endTask: Could not end task: ${error.message}`;
            sendTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation?.sid || wrappingReservation?.sid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
